import { useEffect, useMemo, useState } from 'react'
import { Loader, SegmentedControl } from '@mantine/core'
import { Bar } from 'react-chartjs-2'
import 'chart.js/auto'
import dayjs from '@/lib/dayjs'

import ScrollableScreen from '@/components/ui/ScrollableScreen'
import DocumentTitle from '@/components/ui/DocumentTitle'
import RouterLink from '@/components/ui/RouterLink'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import { StatTiles, StatTile, Delta, compact, pct, barScales, dataset } from '@/components/support/ai/StatTiles'
import { shortModel } from '@/components/support/ai/EvalRunDetail'

const RANGES = [
    { value: '30', label: '30 jours' },
    { value: '90', label: '90 jours' },
    { value: '180', label: '6 mois' },
]

// Palette catégorielle STABLE par version : la plus récente prend l'accent
// bleu, les précédentes remontent la palette ; « 0 » = avant versionnement.
const VERSION_PALETTE = ['#2a78d6', '#8b7fd6', '#2aa889', '#d97706', '#b45309']
const PRE_VERSIONING = '#d5dae1'

const versionColors = (dailyRows, key = 'by_version') => {
    const ids = [...new Set(dailyRows.flatMap((d) => Object.keys(d[key] ?? {})))]
        .map(Number)
        .sort((a, b) => b - a)
    const colors = {}
    let i = 0
    ids.forEach((id) => {
        colors[id] = id === 0 ? PRE_VERSIONING : VERSION_PALETTE[i++ % VERSION_PALETTE.length]
    })
    return { ids, colors }
}

const VersionLegend = ({ ids, colors }) => (
    <div className="ml-auto flex items-center gap-4 text-[11px] text-gray-500">
        {ids.map((id) => (
            <span key={id} className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: colors[id] }} />
                {id === 0 ? 'avant versionnement' : `v${id}`}
            </span>
        ))}
    </div>
)

/** Chip « Politique v7 → » : la config servie en ce moment, en lien vers sa page. */
const VersionChip = ({ versionId, model }) => (
    <RouterLink
        href={versionId ? `/support/ai/policies/${versionId}` : '/support/ai/policies'}
        className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] text-blue-700 no-underline hover:bg-blue-100"
    >
        Politique v{versionId} →
        <span className="text-blue-400">
            · {shortModel(model)}
        </span>
    </RouterLink>
)

/**
 * Le KPI email : la part des messages envoyés par l'équipe qui reprennent un
 * brouillon IA — % par jour, chaque barre découpée par la CONTRIBUTION de
 * chaque version au taux du jour (la hauteur totale reste le taux du jour).
 */
const UsageRateChart = ({ kpi }) => {
    const daily = kpi?.daily ?? []
    const { ids, colors } = useMemo(() => versionColors(daily), [daily])
    if (!daily.length || !kpi.team_messages) return null

    return (
        <div className="rounded-xl border border-gray-200/70 bg-white px-4 py-3">
            <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-900">Réponses IA utilisées · % par jour, par version</span>
                <VersionLegend ids={ids} colors={colors} />
            </div>
            <div className="mt-1 flex items-baseline gap-1">
                <span className="text-2xl font-semibold text-gray-900">{kpi.rate != null ? pct(kpi.rate) : '—'}</span>
                <span className="text-xs text-gray-400">
                    % sur la période — {compact(kpi.from_suggestion)} sur {compact(kpi.team_messages)} messages envoyés (hors bot)
                </span>
            </div>
            <div className="mt-2 h-44">
                <Bar
                    data={{
                        labels: daily.map((d) => dayjs(d.day).format('D MMM')),
                        datasets: ids.map((id) =>
                            dataset(id === 0 ? 'avant versionnement' : `v${id}`, daily.map((d) => d.by_version?.[id] ?? null), colors[id])
                        ),
                    }}
                    options={{
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { display: false },
                            tooltip: {
                                callbacks: {
                                    label: (ctx) => ` ${ctx.dataset.label} : ${pct(ctx.raw)} pts`,
                                    footer: (items) => {
                                        const d = daily[items[0].dataIndex]
                                        return `Taux du jour : ${pct(d.rate)} % (${d.from_suggestion}/${d.team_messages} messages)`
                                    },
                                },
                            },
                        },
                        scales: barScales({ callback: (v) => `${v} %` }),
                    }}
                />
            </div>
            <p className="mt-1.5 text-[10.5px] text-gray-400">
                Un jour de bascule, la barre se découpe selon la part de chaque version dans les réponses utilisées ; la hauteur totale
                reste le taux du jour. Le volume et le détail proposé/escaladé se lisent sur la page de chaque version.
            </p>
        </div>
    )
}

const ADOPTION_COLORS = { verbatim: '#2a78d6', edited: '#7fabe3', rewritten: '#d5dae1' }

const AdoptionBand = ({ adoption }) => {
    const decided = adoption?.decided ?? 0
    if (!decided) return null
    const segments = [
        { key: 'verbatim', label: 'telles quelles', n: adoption.verbatim },
        { key: 'edited', label: 'retouchées', n: adoption.edited },
        { key: 'rewritten', label: 'réécrites', n: adoption.rewritten },
    ]
        .filter((s) => s.n > 0)
        .map((s) => ({ ...s, pct: (100 * s.n) / decided }))

    return (
        <div className="rounded-xl border border-gray-200/70 bg-white px-4 py-3">
            <div className="flex items-baseline justify-between gap-2">
                <div className="text-[11px] text-gray-400">Adoption des brouillons</div>
                <div className="text-[10px] text-gray-400">
                    {adoption.delivered} déposés en note
                    {adoption.no_reply > 0 && ` · ${adoption.no_reply} sans réponse`}
                    {adoption.pending > 0 && ` · ${adoption.pending} en attente`}
                </div>
            </div>
            <div className="mt-1 flex items-baseline gap-1">
                <span className="text-2xl font-semibold text-gray-900">{adoption.used_rate}</span>
                <span className="text-xs text-gray-400">% des brouillons utilisés (tels quels ou retouchés)</span>
            </div>
            <div className="mt-2 flex h-2 gap-0.5 overflow-hidden rounded-full">
                {segments.map((s) => (
                    <span key={s.key} style={{ width: `${s.pct}%`, backgroundColor: ADOPTION_COLORS[s.key] }} />
                ))}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
                {segments.map((s) => (
                    <span key={s.key} className="inline-flex items-center gap-1.5 text-[10px] text-gray-500">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ADOPTION_COLORS[s.key] }} />
                        {Math.round(s.pct)} % {s.label} ({s.n})
                    </span>
                ))}
            </div>
        </div>
    )
}

/** Conversations chat par jour, empilées par version (un volume, lui, s'empile tel quel). */
const ChatDailyChart = ({ daily }) => {
    const { ids, colors } = useMemo(() => versionColors(daily ?? []), [daily])
    if (!daily?.length) return null
    return (
        <div className="rounded-xl border border-gray-200/70 bg-white px-4 py-3">
            <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-900">Conversations par jour · par version</span>
                <VersionLegend ids={ids} colors={colors} />
            </div>
            <div className="mt-3 h-32">
                <Bar
                    data={{
                        labels: daily.map((d) => dayjs(d.day).format('D MMM')),
                        datasets: ids.map((id) =>
                            dataset(id === 0 ? 'avant versionnement' : `v${id}`, daily.map((d) => d.by_version?.[id] ?? null), colors[id])
                        ),
                    }}
                    options={{ maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: barScales() }}
                />
            </div>
        </div>
    )
}

export default function SupportAiStats() {
    const { requestGet } = useAxiosApi()
    const [days, setDays] = useState('30')
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let active = true
        setLoading(true)
        requestGet('/support/ai/overview', { days })
            .then(({ data }) => active && setData(data))
            .catch(() => active && setData(null))
            .finally(() => active && setLoading(false))
        return () => {
            active = false
        }
    }, [days])

    const breadcrumbs = [{ label: 'Support IA' }, { label: 'Statistiques' }]
    const rightSection = <SegmentedControl size="xs" value={days} onChange={setDays} data={RANGES} />

    return (
        <ScrollableScreen breadcrumbs={breadcrumbs} rightSection={rightSection}>
            <DocumentTitle title="BO - Statistiques IA - Baltic Watches" />

            {loading ? (
                <div className="flex justify-center py-20">
                    <Loader />
                </div>
            ) : !data ? (
                <p className="px-6 py-10 text-gray-400">Statistiques indisponibles.</p>
            ) : (
                <main className="@container w-full">
                    <div className="mx-auto max-w-5xl space-y-8 p-5">
                        <section className="flex flex-col gap-3">
                            <div className="flex flex-wrap items-baseline gap-3">
                                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Agent email</span>
                                <VersionChip
                                    versionId={data.active_version_id}
                                    model={data.email_config?.model}
                                />
                                <span className="text-[11.5px] text-gray-300">
                                    brouillons déposés en note interne dans Gorgias · la période peut couvrir plusieurs versions
                                </span>
                            </div>

                            <StatTiles>
                                <StatTile
                                    label="Réponses IA utilisées"
                                    value={data.team_kpi?.rate != null ? pct(data.team_kpi.rate) : '—'}
                                    unit="%"
                                    note={`${compact(data.team_kpi?.from_suggestion)} sur ${compact(data.team_kpi?.team_messages)} messages équipe`}
                                    points={(data.team_kpi?.daily ?? []).map((d) => d.rate ?? 0)}
                                />
                                <StatTile
                                    label="Suggestions par jour"
                                    value={compact(data.volume?.per_day)}
                                    note={`${compact(data.volume?.total)} sur ${data.period_days} jours · ${data.volume?.reply_rate ?? '—'} % avec brouillon`}
                                    points={(data.daily ?? []).map((d) => d.total)}
                                    delta={<Delta current={data.volume?.per_day} previous={data.previous?.per_day} />}
                                />
                                <StatTile
                                    label="Coût mensuel projeté"
                                    value={data.usage?.cost_monthly_estimate != null ? `${data.usage.cost_monthly_estimate} $` : '—'}
                                    note={
                                        data.usage?.measured_suggestions
                                            ? `${data.usage.cost_per_suggestion} $/suggestion · ${data.usage.cache_read_rate ?? 0} % en cache`
                                            : 'metrics pas encore collectées'
                                    }
                                />
                                <StatTile
                                    label="Escaladées (none)"
                                    value={data.volume?.reply_rate != null ? 100 - data.volume.reply_rate : '—'}
                                    unit="%"
                                    note="laissées à l'équipe sans brouillon"
                                    delta={
                                        <Delta
                                            current={data.volume?.reply_rate != null ? 100 - data.volume.reply_rate : null}
                                            previous={data.previous?.reply_rate != null ? 100 - data.previous.reply_rate : null}
                                            unit="pts"
                                        />
                                    }
                                />
                            </StatTiles>

                            <UsageRateChart kpi={data.team_kpi} />
                            <AdoptionBand adoption={data.adoption} />
                        </section>

                        <section className="flex flex-col gap-3">
                            <div className="flex flex-wrap items-baseline gap-3">
                                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Chat du site</span>
                                <VersionChip
                                    versionId={data.active_version_id}
                                    model={data.chat?.model}
                                />
                                <span className="text-[11.5px] text-gray-300">tokens seulement, le coût n'est pas relu pour le chat</span>
                            </div>
                            <StatTiles>
                                <StatTile
                                    label="Conversations par jour"
                                    value={compact(data.chat?.conversations_per_day)}
                                    note={`${compact(data.chat?.conversations)} sur ${data.period_days} jours`}
                                    points={(data.chat?.daily ?? []).map((d) => d.conversations)}
                                    delta={<Delta current={data.chat?.conversations_per_day} previous={data.chat?.previous_per_day} />}
                                />
                                <StatTile
                                    label="Réponses par conversation"
                                    value={compact(data.chat?.turns_per_conversation)}
                                    note={`${compact(data.chat?.turns)} réponses au total`}
                                />
                                <StatTile
                                    label="Renvoi vers l'email"
                                    value={data.chat?.handover_rate ?? '—'}
                                    unit="%"
                                    note="réponses finissant par « écrivez-nous »"
                                />
                                <StatTile
                                    label="Tokens par réponse"
                                    value={compact(data.chat?.tokens_per_turn)}
                                    note={`${data.chat?.cache_read_rate ?? 0} % en cache · ${
                                        data.chat?.ttft_ms != null ? `${(data.chat.ttft_ms / 1000).toFixed(1).replace('.', ',')} s` : '—'
                                    } avant le premier mot`}
                                />
                            </StatTiles>
                            <ChatDailyChart daily={data.chat?.daily} />
                        </section>
                    </div>
                </main>
            )}
        </ScrollableScreen>
    )
}
